import { apiUrl } from './api_config'

const API_BASE_URL = apiUrl('/old-payments')
const GET_OLD_PAYMENTS_ENDPOINT = `${API_BASE_URL}/all`
const GET_TOTAL_OLD_PAYMENTS_ENDPOINT = `${API_BASE_URL}/totalOldPayments`
const ADD_OLD_PAYMENT_ENDPOINT = `${API_BASE_URL}/add`
const UPDATE_OLD_PAYMENT_ENDPOINT = `${API_BASE_URL}/update`
const DELETE_OLD_PAYMENT_ENDPOINT = `${API_BASE_URL}/delete`

async function request(url, options = {}) {
  const response = await fetch(url, {
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    ...options,
  })

  if (!response.ok) {
    let message = `Old payment request failed (${response.status}) at ${url}`
    try {
      const error = await response.json()
      message = error.message || error.error || message
    } catch {
      // Keep the HTTP status message when the server does not return JSON.
    }
    throw new Error(message)
  }

  return response.status === 204 ? null : response.json()
}

function findPayments(data) {
  if (Array.isArray(data)) return data
  if (!data || typeof data !== 'object') return []

  for (const key of ['payments', 'paymentList', 'results', 'items', 'data']) {
    const payments = findPayments(data[key])
    if (payments.length || Array.isArray(data[key])) return payments
  }

  return []
}

function findNextCursor(data) {
  if (!data || typeof data !== 'object') return null
  return (
    data.nextCursor ??
    data.next_cursor ??
    data.nextPageToken ??
    data.next_page_token ??
    data.next ??
    findNextCursor(data.payments) ??
    findNextCursor(data.paymentList) ??
    findNextCursor(data.results) ??
    findNextCursor(data.items) ??
    findNextCursor(data.data) ??
    findNextCursor(data.pagination) ??
    findNextCursor(data.meta) ??
    null
  )
}

const addParam = (params, key, value) => {
  if (value === undefined || value === null || value === '') return
  params.set(key, String(value))
}

export async function getOldPayments({
  cursor = null,
  page = 1,
  fromDate = '',
  toDate = '',
  fromAmount = '',
  toAmount = '',
  customer = '',
  customerType = '',
  orderType = '',
  salesExecutive = '',
  paymentMethod = '',
} = {}) {
  const params = new URLSearchParams({ limit: '20' })
  if (cursor) params.set('cursor', cursor)
  else params.set('page', String(page))
  addParam(params, 'fromDate', fromDate)
  addParam(params, 'toDate', toDate)
  addParam(params, 'fromAmount', fromAmount)
  addParam(params, 'toAmount', toAmount)
  addParam(params, 'customer', customer)
  addParam(params, 'customerType', customerType)
  addParam(params, 'orderType', orderType)
  addParam(params, 'salesExecutive', salesExecutive)
  addParam(params, 'paymentMethod', paymentMethod)

  const data = await request(`${GET_OLD_PAYMENTS_ENDPOINT}?${params}`)
  const payload = data?.data && !Array.isArray(data.data) ? data.data : data
  const payments = findPayments(data)
  const nextCursor = findNextCursor(data)
  const currentPage = Number(
    payload?.page ?? payload?.currentPage ?? payload?.current_page ?? page,
  )
  const hasNext = Boolean(
    payload?.hasNext ??
    payload?.has_next ??
    data?.hasNext ??
    data?.has_next ??
    nextCursor,
  )

  return {
    payments,
    nextCursor,
    nextPage: hasNext && !nextCursor ? currentPage + 1 : null,
    currentPage,
  }
}

export async function getTotalOldPayments() {
  const data = await request(GET_TOTAL_OLD_PAYMENTS_ENDPOINT)
  const payload = data?.data && !Array.isArray(data.data) ? data.data : data

  if (typeof data === 'number' || typeof data === 'string') {
    return Number(data) || 0
  }

  return Number(
    payload?.totalOldPayments ??
      payload?.total_old_payments ??
      payload?.total ??
      data?.totalOldPayments ??
      data?.total_old_payments ??
      data?.total ??
      0,
  )
}

export async function createOldPayment(payment) {
  const data = await request(ADD_OLD_PAYMENT_ENDPOINT, {
    method: 'POST',
    body: JSON.stringify(payment),
  })
  return data?.data || data
}

export async function updateOldPayment(paymentId, payment) {
  const data = await request(
    `${UPDATE_OLD_PAYMENT_ENDPOINT}/${encodeURIComponent(paymentId)}`,
    {
      method: 'PUT',
      body: JSON.stringify(payment),
    },
  )
  return data?.data || data
}

export async function deleteOldPayment(paymentId) {
  await request(
    `${DELETE_OLD_PAYMENT_ENDPOINT}/${encodeURIComponent(paymentId)}`,
    { method: 'DELETE' },
  )
}
